import { axiosInstance, axiosInstanceV2, axiosInstanceV3 } from '../axios.ts';
import {
  getForcedPaginatedEndpoint,
  getNonPaginatedEndpoint,
  getPaginatedEndpoint,
  snakeCaseDictToCamelCaseDict,
} from '../utils.ts';
import {
  AgreementNotificationSettings,
  AgreementNotificationSettingsPayload,
  IAgreement,
  IAgreementEmail,
  IAgreementErpPlan,
  IAgreementLanding,
  IAgreementMagazine,
  IAgreementNotification,
  IAgreementPromocode,
  IAgreementPromocodeBatch,
  IAgreementStaff,
  ICreateAgreementPromocodeBatch,
  ICreateAgreementStaff,
  ICreateBannerData,
  ICreateMapIntegrationPlanPayload,
  IErpPlan,
  IErpPlansReport,
  IErpPlansResponse,
  IReportItem,
  ISyncReportResponse,
  IImportation,
  IMapIntegrationPlanItem,
  IModules,
  IPlan,
  IProduct,
  IPromocodeByTag,
  IReport,
  ISubscription,
  IUsage,
  IReceivedBookStaff,
  IReceivedBookStaffListResponse,
  IMonthlyReportResponse,
  IIntegrationDataDisplayResponse,
  IBookStaffDownload,
  ISyncClientMetadata,
  ISyncClientMetadataDetail,
  ISyncClientPlanMapped,
  ISyncClientSubscriptionMapped,
  ISyncClientsInvalidResponse,
  ISyncClientsMetadataExportPayload,
  ISyncClientsMetadataStatistics,
} from './types.ts';

import { IBanner, IDiscount, ICompany, IUser, PaginatedResponse, RequestParameters, IBook } from '../types';

export async function getImportation(params: RequestParameters): Promise<PaginatedResponse<IImportation>> {
  return getPaginatedEndpoint(`/agreement_staff/request-prospected-client/`, params);
}

export async function getReport(year: number | string): Promise<IReport> {
  return (await getNonPaginatedEndpoint(`/agreement/report?year=${year}`)) as IReport;
}

export async function getReports(): Promise<IReport> {
  return (await getNonPaginatedEndpoint(`/report`)) as IReport;
}

export async function getReportsList(
  params: RequestParameters & { type?: string } = {}
): Promise<PaginatedResponse<IReportItem>> {
  return getPaginatedEndpoint(`/report`, params);
}

export async function getProducts(): Promise<IProduct> {
  const response = await getNonPaginatedEndpoint(`/agreement_staff/subscription_mapintegrationplan/`, {
    page_size: 1000,
  });
  return response as IProduct;
}

export async function getModules(): Promise<IModules> {
  const response = await getNonPaginatedEndpoint(`/agreement_staff/modules/`, {
    page_size: 1000,
  });
  return (response as { results: IModules }).results;
}

export async function getAgreementData(): Promise<IAgreement> {

  return (await getNonPaginatedEndpoint(`/agreement_staff/agreement/`)) as IAgreement;
}

export async function getAgreementInfo(): Promise<AgreementNotificationSettings> {
  return (await getNonPaginatedEndpoint(`/agreement/info/`)) as AgreementNotificationSettings;
}

export async function updateAgreementInfo(
  data: AgreementNotificationSettingsPayload
): Promise<AgreementNotificationSettings> {
  return axiosInstance
    .put(`/agreement/info/`, data)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as AgreementNotificationSettings);
}

export async function getSubscriptions(params: RequestParameters = {}): Promise<PaginatedResponse<ISubscription>> {
  return getPaginatedEndpoint(`/agreement_staff/subscriptions/?`, params);
}

export async function getUsers(params: RequestParameters = {}): Promise<PaginatedResponse<IUser>> {
  return getPaginatedEndpoint(`/agreement_staff/users/?`, params);
}

export async function getUsage(params: RequestParameters = {}): Promise<PaginatedResponse<IUsage>> {
  return getPaginatedEndpoint(`/agreement_staff/usage/?`, params);
}

export async function deleteSubscription(id: number | string): Promise<void> {
  return axiosInstance.delete(`/agreement_staff/subscriptions/${id}/`);
}

export async function getAgreementPlans(params: RequestParameters = {}): Promise<IPlan[]> {
  return axiosInstance.get(`/agreement_staff/plans/`, { params }).then(({ data }) => data);
}

export async function getProductsV2(params: RequestParameters = {}): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: Product[];
}> {
  return axiosInstance.get(`/product_staff/`, { params }).then(({ data }) => data);
}

export async function getDefaultProductsV2(params: RequestParameters = {}): Promise<Product[]> {
  return axiosInstance.get(`/default_product_staff/`, { params }).then(({ data }) => data);
}
export async function getPlansByIntegrationId(params: RequestParameters = {}): Promise<IPlan[]> {
  return axiosInstance.get(`/map_integration_plan_staff/`, { params }).then(({ data }) => data);
}

export async function createSubscriptionsCode(external_code: string[], planID: number | string) {
  const data = {
    external_codes: external_code,
    plan_id: planID,
  };

  return axiosInstance.post(`/agreement_staff/subscriptions/`, data);
}

export async function createSubscriptions(cpfs: string[], planID: number | string) {
  const data = {
    cpfs: cpfs,
    plan_id: planID,
  };

  return axiosInstance.post(`/agreement_staff/subscriptions/`, data);
}

export async function createBulkSubscriptions(data: {
  cpfs?: Array<{ cpf: string; plans_id: (number | string)[] }>;
  external_codes?: Array<{ code: string; plans_id: (number | string)[] }>;
}) {
  return axiosInstance.post(`/agreement_staff/subscriptions/`, data);
}

export async function deleteCpfsSubscriptions(cpfs: string[]) {
  const data = {
    cpfs: cpfs,
  };

  return axiosInstance.delete(`/agreement_staff/subscriptions/`, {
    data: data,
  });
}

export async function createBanner(data: ICreateBannerData) {
  const formData = new FormData();
  formData.append('name', data.name);
  if (data.image) formData.append('image', data.image);
  if (data.imageMobile) formData.append('image_mobile', data.imageMobile);
  if (data.url) formData.append('url', data.url);
  formData.append('type', data.type);

  return axiosInstance.post('banner/agreement_staff/', formData);
}

export async function deleteAgreementBanner(id: number) {
  return axiosInstance.delete(`banner/agreement_staff/${id}/`);
}

export async function getBanners(): Promise<PaginatedResponse<IBanner>> {
  return getForcedPaginatedEndpoint(`/banner/agreement_staff/?`);
}

export async function getDiscounts(search?: string): Promise<PaginatedResponse<IDiscount>> {
  return axiosInstance
    .get(`/discount_staff/?page_size=100${typeof search !== 'undefined' ? `&search=${search}` : ''}`)
    .then(({ data }) => data);
}

export function toggleBanner(id: number) {
  return axiosInstance.post(`/banner/agreement_staff/${id}/toggle_partiu_banner/`);
}

export async function getAgreementCompanies(params: RequestParameters = {}): Promise<PaginatedResponse<ICompany>> {
  return getPaginatedEndpoint(`/agreement_staff/companies/?`, params);
}

export async function getAgreementOnlineCompanies(
  params: RequestParameters = {}
): Promise<PaginatedResponse<ICompany>> {
  return getPaginatedEndpoint(`/promotion/agreement_staff/online_promotions/?`, params);
}

export async function toggleAgreementOnlineCompany(companyID: number) {
  return axiosInstance
    .post(`/promotion/agreement_staff/${companyID}/toggle_partiu_promotion/`)
    .then(({ data }) => data);
}

export async function getAgreementPromocodeBatchs(
  params: RequestParameters
): Promise<PaginatedResponse<IAgreementPromocodeBatch>> {
  return getPaginatedEndpoint(`/agreement_staff/promocode_batchs/?`, params);
}

export async function getAgreementPromocodeBatchsAggByTags(
  params: RequestParameters
): Promise<PaginatedResponse<IPromocodeByTag>> {
  return getPaginatedEndpoint(`/agreement/report/tags?`, params);
}

export async function createAgreementPromocodeBatch(data: ICreateAgreementPromocodeBatch) {
  return axiosInstance.post(`/agreement_staff/promocode_batchs/`, {
    name: data.name + '-' + new Date().toISOString(),
    quantity: data.quantity,
    tag: data.tag,
    valid_until: data.validUntil.split('T')[0],
  });
}

export function getAgreementPromocodesFromBatch(batch: string): Promise<IAgreementPromocode[]> {
  return getNonPaginatedEndpoint(`/agreement_staff/promocodes/?name=${encodeURI(batch)}`) as Promise<
    IAgreementPromocode[]
  >;
}

export async function getAgreementMagazines(params: RequestParameters): Promise<PaginatedResponse<IAgreementMagazine>> {
  return getPaginatedEndpoint(`/magazine/magazines_staff/?`, params, axiosInstanceV2);
}

export async function createAgreementMagazine(agreementID: number) {
  return axiosInstanceV2.post(`/magazine/magazines_staff/`, {
    agreement_id: agreementID,
  });
}

export async function getAgreementPushNotifications(): Promise<PaginatedResponse<IAgreementNotification>> {
  return getForcedPaginatedEndpoint(`/agreement_staff/notifications/`);
}

export async function createAgreementPushNotification(data: IAgreementNotification) {
  return axiosInstance.post(`/agreement_staff/notifications/`, data);
}

export function deleteAgreementPushNotification(id: number) {
  return axiosInstance.delete(`/agreement_staff/notifications/?id=${id}`);
}

export function getAgreementStaffs(params: RequestParameters): Promise<PaginatedResponse<IAgreementStaff>> {
  return getPaginatedEndpoint(`/agreement_staff/accounts/?`, params);
}

export function createAgreementStaff(data: ICreateAgreementStaff) {
  return axiosInstance.post(`/agreement_staff/accounts/`, {
    full_name: data.fullName,
    agreement_staff_permissions: data.agreementStaffPermissions,
    password: data.password,
    email: data.email,
    agreement_id: data.agreementID,
  });
}

export function updateAgreementStaff(data: ICreateAgreementStaff) {
  const payload = {
    full_name: data.fullName,
    agreement_staff_permissions: data.agreementStaffPermissions,
    password: data.password,
    email: data.email,
  };

  //@ts-expect-error - password is optional and can be deleted from payload
  if (!payload.password) delete payload['password'];

  return axiosInstance.put(`/agreement_staff/accounts/${data.id}/`, payload);
}

export function deleteAgreementStaff(id: number) {
  return axiosInstance.delete(`/agreement_staff/accounts/${id}/`);
}

export function getAgreementLandingInfo(): Promise<IAgreementLanding> {
  return getNonPaginatedEndpoint(`/agreement/info`) as Promise<IAgreementLanding>;
}

export type IAgreementLandingInfoPayload = Pick<
  IAgreementLanding,
  'email' | 'telephone' | 'cellphone' | 'contactLink' | 'footerText'
>;

export function updateAgreementLandingInfo(data: Partial<IAgreementLandingInfoPayload>): Promise<IAgreementLanding> {
  // A API lê snake_case; só envia o que veio para não apagar campos de outra tela.
  const body = Object.fromEntries(
    Object.entries({
      email: data.email,
      telephone: data.telephone,
      cellphone: data.cellphone,
      contact_link: data.contactLink,
      footer_text: data.footerText,
    }).filter(([, value]) => value !== undefined)
  );
  return axiosInstance
    .put(`/agreement/info/`, body)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IAgreementLanding);
}

export function getAgreementPermissions(): Promise<string[]> {
  return getNonPaginatedEndpoint('/agreement_staff/permissions') as Promise<string[]>;
}

export async function sendAgreementNotificationEmail(data: IAgreementEmail) {
  return axiosInstance.post('/agreement_staff/notification_emails/', {
    title: data.title,
    text: data.text.replaceAll('\n', '<br/>'),
    subject: data.subject,
    emails_list: data.emailList,
  });
}

export async function createAgreementCompany(data: Record<string, unknown>) {
  const formData = new FormData();

  Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .forEach(([key, value]: [string, unknown]) => formData.append(key, value as string | Blob));

  return axiosInstance.post(`/agreement_staff/companies-v2/`, formData);
}

export async function getReceivedBooksStaff(
  params: RequestParameters = {}
): Promise<IReceivedBookStaffListResponse> {
  return getPaginatedEndpoint(`/received_book_staff/?`, params) as Promise<IReceivedBookStaffListResponse>;
}

export async function exportReceivedBooksStaff(params: RequestParameters = {}): Promise<IReceivedBookStaff[]> {
  // Expect server to return a non-paginated array for export based on filters
  return axiosInstance
    .post(`/received_book_staff/export/`, params)
    .then(({ data }) => data as IReceivedBookStaff[]);
}

export async function getBookStaffs(params: RequestParameters = {}): Promise<PaginatedResponse<IBook>> {
  return getPaginatedEndpoint(`/book_staff/?`, params);
}

export async function getBookStaffDownload(bookId: number | string): Promise<IBookStaffDownload> {
  return getNonPaginatedEndpoint(
    `/bookstore/books/${bookId}/staff-download/`,
    {},
    axiosInstanceV3
  ) as Promise<IBookStaffDownload>;
}

export async function getMonthlyReportBooks(params: RequestParameters = {}): Promise<IMonthlyReportResponse> {

  if (!params.book__type) {
    delete params.book__type;
  }
  return getNonPaginatedEndpoint(`/received_book_staff/monthly-report/`, params) as Promise<IMonthlyReportResponse>;
}

export async function testIntegrationVinculate(agreementId: number, cpf: string): Promise<IIntegrationDataDisplayResponse> {
  return axiosInstanceV2
    .post(`/agreement/vinculate/?agreement_id=${agreementId}&integrationError=true`, {
      cpf: cpf.replace(/\D/g, ''),
    })
    .then(({ data }) => data);
}

function extractErpPlansResponse(data: unknown): IErpPlansResponse {
  let plans: IErpPlan[] = [];
  let report: IErpPlansReport | undefined = undefined;

  if (Array.isArray(data)) {
    plans = data;
  } else if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) plans = obj.data as IErpPlan[];
    else if (Array.isArray(obj.plans)) plans = obj.plans as IErpPlan[];
    else if (Array.isArray(obj.results)) plans = obj.results as IErpPlan[];
    report = (obj.report ?? obj.Report ?? undefined) as IErpPlansReport | undefined;

    // Alguns backends retornam o tipo de mapeamento no nível raiz (ex.: map_type.slug === 'ID-PLANO').
    // Propagamos esse slug para cada plano como type_mapping, caso ainda não exista.
    const mapType = obj.map_type as { slug?: string } | undefined;
    const typeMappingSlug = mapType?.slug;
    if (typeMappingSlug && Array.isArray(plans)) {
      const normalizedSlug = typeMappingSlug.toString();
      plans = plans.map((plan) => ({
        ...plan,
        type_mapping: plan.type_mapping ?? normalizedSlug,
      }));
    }
  }

  return { plans, report };
}

export async function getPlansErp(): Promise<IErpPlansResponse> {
  return axiosInstance.get(`/get_plans_erp/`).then(({ data }) => extractErpPlansResponse(data));
}

export async function syncPlansErp(): Promise<ISyncReportResponse> {
  const { data } = await axiosInstance.post<ISyncReportResponse>(`/report/`, {
    type: 'erp-plans-dashboard',
    name: `Sincronização ERP - ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
  });
  return data ?? {};
}

export function normalizeErpPlan(plan: IErpPlan): IAgreementErpPlan {
  const rawValue = plan.value;
  return {
    id: plan.id,
    slug: plan.slug,
    name: plan.name || plan.name_plan || plan.slug,
    price: rawValue ? parseFloat(String(rawValue).replace(',', '.')) : undefined,
    value: rawValue,
    erp: plan.erp,
    active: plan.active,
    count_clients: plan.count_clients,
    count_clients_active: plan.count_clients_active,
    type_mapping: plan.type_mapping,
    mapped_by_id: plan.mapped_by_id,
    mapped_by_price: plan.mapped_by_price,
    mapped_by_combo: plan.mapped_by_combo,
    mapped_by_product: plan.mapped_by_product,
  };
}

export async function getAllMapIntegrationPlansStaff(): Promise<IMapIntegrationPlanItem[]> {
  return axiosInstance.get(`/agreement_staff/subscription_mapintegrationplan/`).then(({ data }) => {
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    if (data?.data && Array.isArray(data.data)) return data.data;
    return [];
  });
}

export async function createMapIntegrationPlanStaff(
  data: ICreateMapIntegrationPlanPayload
): Promise<IMapIntegrationPlanItem> {
  return axiosInstance
    .post(`/agreement_staff/subscription_mapintegrationplan/`, data)
    .then(({ data }) => data);
}

export async function deleteMapIntegrationPlanStaff(id: number): Promise<void> {
  return axiosInstance.delete(`/map_integration_plan_staff/${id}/`);
}

export type ISyncClientsMetadataListResponse = PaginatedResponse<ISyncClientMetadata> & {
  message?: string;
  syncStatus?: string;
  statistics?: ISyncClientsMetadataStatistics;
};

export async function getSyncClientsMetadata(
  params: RequestParameters = {}
): Promise<ISyncClientsMetadataListResponse> {
  return getPaginatedEndpoint(`/agreement_staff/sync-clients-metadata/`, params);
}

export async function getSyncClientMetadataDetail(
  id: number | string
): Promise<ISyncClientMetadataDetail | ISyncClientsInvalidResponse> {
  const { data } = await axiosInstance.get(`/agreement_staff/sync-clients-metadata/${id}/`);

  // Raw response, not yet camelCased (this function deliberately skips the
  // generic transform below to preserve `data.metadata`'s ERP key casing) —
  // check the snake_case `sync_status`, not `syncStatus`.
  if (data?.sync_status === 'error') {
    return {
      message: data.message,
      syncStatus: 'error',
    };
  }

  const plansMapped: ISyncClientPlanMapped[] = (data.plans_mapped || []).map((p: Record<string, unknown>) => ({
    slug: p.slug as string,
    startDate: p.start_date as string,
    validUntil: p.valid_until as string,
  }));

  const subscriptionsMapped: ISyncClientSubscriptionMapped[] = (data.subscriptions_mapped || []).map(
    (s: Record<string, unknown>) => ({
      active: s.active as boolean,
      planId: s.plan_id as number,
      productId: s.product_id as number,
      startDate: s.start_date as string,
      validUntil: (s.valid_until as string | null) ?? null,
      planName: (s.plan_name as string | null) ?? null,
      productName: (s.product_name as string | null) ?? null,
    })
  );

  return {
    id: data.id,
    document: data.document,
    telephone: data.telephone,
    email: data.email,
    password: data.password,
    active: data.active,
    synced: data.synced,
    metadata: data.metadata || [],
    slugsMapped: data.slugs_mapped || [],
    plansMapped,
    subscriptionsMapped,
    errorMessage: data.error_message,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export async function generateSyncClientsMetadataReport(
  payload: ISyncClientsMetadataExportPayload = {}
): Promise<{ message: string }> {
  const body: Record<string, unknown> = {
    type: 'sync-clients-metadata-dashboard',
  };

  if (payload.name) body.name = payload.name;
  if (payload.search) body.search = payload.search;
  if (payload.active !== undefined) body.active = payload.active;
  if (payload.synced !== undefined) body.synced = payload.synced;
  if (payload.withoutPlansMapped !== undefined) body.without_plans_mapped = payload.withoutPlansMapped;
  if (payload.withoutSlugsMapped !== undefined) body.without_slugs_mapped = payload.withoutSlugsMapped;
  if (payload.withoutSubscriptionsMapped !== undefined) {
    body.without_subscriptions_mapped = payload.withoutSubscriptionsMapped;
  }
  if (payload.errorMessage !== undefined) body.error_message = payload.errorMessage;

  const { data } = await axiosInstance.post(`/report/`, body);
  return data ?? {};
}
